import { useState, useEffect, useCallback } from 'react';
import api from '../../utils/api';
import { Layout } from '../../components/Layout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import { ArrowClockwise, Factory, Plus, Warning } from '@phosphor-icons/react';

const CARD_META = [
  { key: 'total_pipeline', label: 'Total Pipeline', color: 'text-zinc-800', bg: 'bg-white' },
  { key: 'material_pending', label: 'Material Pending', color: 'text-amber-800', bg: 'bg-amber-50 border-amber-200' },
  { key: 'ready_to_assign', label: 'Ready to Assign', color: 'text-emerald-800', bg: 'bg-emerald-50 border-emerald-200' },
  { key: 'in_production', label: 'In Production', color: 'text-blue-800', bg: 'bg-blue-50 border-blue-200' },
  { key: 'delivery_upcoming', label: 'Upcoming Delivery', color: 'text-purple-800', bg: 'bg-purple-50 border-purple-200' },
];

const BAND_COLORS = {
  green: 'bg-emerald-500 text-white',
  orange: 'bg-amber-500 text-white',
  red: 'bg-red-500 text-white',
};

function ProcurementDashboard() {
  const [data, setData] = useState(null);
  const [capacity, setCapacity] = useState({ settings: [], current_capacity: 0 });
  const [loading, setLoading] = useState(true);
  const [showCapForm, setShowCapForm] = useState(false);
  const [capForm, setCapForm] = useState({ effective_date: '', windows_per_day: '', notes: '' });
  const [selectedDay, setSelectedDay] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [d, c] = await Promise.all([
        api.get('/api/admin/procurement/dashboard'),
        api.get('/api/admin/production-capacity'),
      ]);
      setData(d.data);
      setCapacity({ settings: c.data.settings || [], current_capacity: c.data.current_capacity || 0 });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveCapacity = async () => {
    const wpd = parseInt(capForm.windows_per_day);
    if (!wpd || wpd <= 0) { toast.error('Windows/day must be positive'); return; }
    if (!capForm.effective_date) { toast.error('Effective date required'); return; }
    try {
      await api.post('/api/admin/production-capacity', {
        effective_date: capForm.effective_date,
        windows_per_day: wpd,
        notes: capForm.notes,
      });
      toast.success('Capacity setting saved');
      setShowCapForm(false);
      setCapForm({ effective_date: '', windows_per_day: '', notes: '' });
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
  };

  if (loading || !data) {
    return <Layout><div className="p-8 text-center text-zinc-400">Loading Procurement Dashboard…</div></Layout>;
  }

  const days = data.calendar?.days || [];

  return (
    <Layout>
      <div className="space-y-6 pb-12" data-testid="procurement-dashboard">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="text-3xl font-light tracking-tight text-zinc-950 flex items-center gap-2">
              <Factory size={30} className="text-orange-500" /> Procurement Dashboard
            </h1>
            <p className="text-sm text-zinc-500 mt-1">
              Live pipeline view with backward-scheduled production calendar. Current capacity: <span className="font-bold text-zinc-900">{capacity.current_capacity} windows/day</span>
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={load}><ArrowClockwise size={14} /></Button>
            <Button onClick={() => setShowCapForm(true)} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="new-capacity-btn">
              <Plus size={14} className="mr-1" /> Capacity Setting
            </Button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="summary-cards">
          {CARD_META.map((m) => (
            <Card key={m.key} className={`border-2 ${m.bg}`}>
              <CardContent className="p-4">
                <div className="text-[10px] uppercase tracking-wider text-zinc-600">{m.label}</div>
                <div className={`text-3xl font-bold mt-1 ${m.color}`} data-testid={`card-${m.key}`}>
                  {data.cards[m.key] || 0}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Production Calendar */}
        <Card className="border-zinc-300 rounded-sm"><CardContent className="p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="text-xs uppercase tracking-wider text-zinc-500">Production Calendar (next 30 days)</div>
            <div className="flex gap-2 text-[10px]">
              <span className="px-2 py-0.5 rounded bg-emerald-500 text-white">Green ≤ 60%</span>
              <span className="px-2 py-0.5 rounded bg-amber-500 text-white">Orange 60–90%</span>
              <span className="px-2 py-0.5 rounded bg-red-500 text-white">Red &gt; 90%</span>
            </div>
          </div>
          <div className="grid grid-cols-7 gap-1" data-testid="calendar-grid">
            {days.map((d) => (
              <button
                key={d.date}
                onClick={() => setSelectedDay(d)}
                data-testid={`day-${d.date}`}
                className={`p-2 rounded-sm text-left transition-all hover:ring-2 hover:ring-orange-300 ${
                  d.is_sunday ? 'bg-zinc-100 opacity-50' :
                  d.band === 'green' ? 'bg-emerald-50 border border-emerald-200' :
                  d.band === 'orange' ? 'bg-amber-50 border border-amber-200' :
                  'bg-red-50 border border-red-200'
                }`}
              >
                <div className="text-[10px] text-zinc-500">{d.day_of_week}</div>
                <div className="text-sm font-bold text-zinc-900">{d.date.slice(-2)}</div>
                <div className={`text-[10px] font-mono mt-1 rounded px-1 inline-block ${BAND_COLORS[d.band]}`}>
                  {Number(d.planned || 0).toFixed(0)}/{d.capacity}
                </div>
                <div className="text-[9px] text-zinc-500 mt-1">{d.utilization_pct}%</div>
              </button>
            ))}
          </div>
        </CardContent></Card>

        {/* Capacity Settings Table */}
        <Card className="border-zinc-300 rounded-sm"><CardContent className="p-0">
          <div className="p-3 text-xs uppercase tracking-wider text-zinc-500 border-b border-zinc-100">Capacity Timeline</div>
          <table className="min-w-full text-sm">
            <thead className="bg-zinc-100 text-zinc-600 uppercase tracking-wider text-[10px]">
              <tr>
                <th className="p-3 text-left">Effective Date</th>
                <th className="p-3 text-right">Windows / Day</th>
                <th className="p-3 text-left">Notes</th>
                <th className="p-3 text-left">Created By</th>
              </tr>
            </thead>
            <tbody>
              {capacity.settings.length === 0 ? (
                <tr><td colSpan={4} className="p-4 text-center text-zinc-400">
                  <div className="flex items-center gap-2 justify-center">
                    <Warning size={16} /> No capacity settings — using default 20 windows/day
                  </div>
                </td></tr>
              ) : capacity.settings.map((s, i) => (
                <tr key={i} className="border-b border-zinc-100">
                  <td className="p-3 font-mono text-xs">{s.effective_date}</td>
                  <td className="p-3 text-right font-bold">{s.windows_per_day}</td>
                  <td className="p-3 text-xs">{s.notes || '—'}</td>
                  <td className="p-3 text-xs text-zinc-500">{s.created_by}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent></Card>
      </div>

      {/* Capacity dialog */}
      <Dialog open={showCapForm} onOpenChange={setShowCapForm}>
        <DialogContent>
          <DialogHeader><DialogTitle>New Production Capacity Setting</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-500">Effective Date*</Label>
              <Input type="date" value={capForm.effective_date} onChange={(e) => setCapForm({ ...capForm, effective_date: e.target.value })} data-testid="cap-date" />
              <div className="text-[10px] text-zinc-500 mt-1">Setting applies to JOs scheduled from this date onwards.</div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-500">Windows / Day*</Label>
              <Input type="number" value={capForm.windows_per_day} onChange={(e) => setCapForm({ ...capForm, windows_per_day: e.target.value })} data-testid="cap-wpd" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-500">Notes</Label>
              <Input value={capForm.notes} onChange={(e) => setCapForm({ ...capForm, notes: e.target.value })} placeholder="e.g. New shift added" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCapForm(false)}>Cancel</Button>
            <Button onClick={saveCapacity} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="cap-save">Save Setting</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Day drill-down dialog */}
      <Dialog open={!!selectedDay} onOpenChange={(o) => !o && setSelectedDay(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Production Load — {selectedDay?.date} ({selectedDay?.day_of_week})</DialogTitle>
          </DialogHeader>
          {selectedDay && (
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-3">
                <div className="p-3 rounded-sm border border-zinc-200 bg-zinc-50">
                  <div className="text-[10px] uppercase tracking-wider text-zinc-500">Capacity</div>
                  <div className="text-2xl font-bold">{selectedDay.capacity}</div>
                </div>
                <div className="p-3 rounded-sm border border-zinc-200 bg-zinc-50">
                  <div className="text-[10px] uppercase tracking-wider text-zinc-500">Planned</div>
                  <div className="text-2xl font-bold">{selectedDay.planned.toFixed(1)}</div>
                </div>
                <div className={`p-3 rounded-sm border ${
                  selectedDay.band === 'green' ? 'border-emerald-200 bg-emerald-50' :
                  selectedDay.band === 'orange' ? 'border-amber-200 bg-amber-50' :
                  'border-red-200 bg-red-50'
                }`}>
                  <div className="text-[10px] uppercase tracking-wider text-zinc-500">Utilization</div>
                  <div className="text-2xl font-bold">{selectedDay.utilization_pct}%</div>
                </div>
              </div>
              <div className="text-xs uppercase tracking-wider text-zinc-500 mt-2">JOs on this day</div>
              {selectedDay.jos?.length === 0 ? (
                <div className="text-sm text-zinc-400 text-center p-4">No JOs scheduled.</div>
              ) : (
                <table className="min-w-full text-sm">
                  <thead className="bg-zinc-100 text-zinc-600 uppercase tracking-wider text-[10px]">
                    <tr>
                      <th className="p-2 text-left">JO Number</th>
                      <th className="p-2 text-left">Customer</th>
                      <th className="p-2 text-right">Windows</th>
                      <th className="p-2 text-left">Procurement</th>
                      <th className="p-2 text-left">Prod Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedDay.jos.map((jo) => (
                      <tr key={jo.jo_number} className="border-b border-zinc-100">
                        <td className="p-2 font-mono text-xs font-bold text-orange-700">{jo.jo_number}</td>
                        <td className="p-2 text-xs">{jo.customer_name}</td>
                        <td className="p-2 text-right font-bold">{jo.windows}</td>
                        <td className="p-2"><Badge className="text-[10px] bg-zinc-200 text-zinc-700">{(jo.procurement_status || '').toUpperCase()}</Badge></td>
                        <td className="p-2"><Badge className="text-[10px] bg-blue-100 text-blue-800">{(jo.assigned_to_production_status || '').toUpperCase()}</Badge></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Layout>
  );
}

export default ProcurementDashboard;
export { ProcurementDashboard };
